// src/components/sections/ProjectHeader.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { getStatusColor } from '../../utils/colors';
import { formatDate } from '../../utils/constants';

const getDirScoreColor = (score) => {
  if (score === null || score === undefined) return '#757575';
  if (score >= 2)    return '#03DAC6';
  if (score >= 0.5)  return '#64B5F6';
  if (score >= -0.5) return '#FFCF44';
  if (score >= -1.5) return '#FF9800';
  return '#CF6679';
};

const getClientSatisfactionColor = (score) => {
  if (score === null || score === undefined) return '#757575';
  if (score >= 4.0) return '#03DAC6';
  if (score >= 3.0) return '#FFCF44';
  return '#CF6679';
};

const ProjectHeader = ({ clientName, status, budgetSpent, totalBudget, budget, startDate, endDate, dirScore, clientSatisfactionScore }) => {
  const formattedStartDate = startDate ? formatDate(startDate) : 'N/A';
  const formattedEndDate   = endDate   ? formatDate(endDate)   : 'N/A';

  // Build hero metric line: prefer raw values, fall back to legacy budget string
  const heroValue   = budgetSpent  ? budgetSpent  : null;
  const heroSubline = totalBudget  ? `of ${totalBudget} budgeted` : null;

  return (
    <Box sx={{ p: 1.5, bgcolor: '#1A1A1A', display: 'flex', flexDirection: 'column', gap: 0.75 }}>

      {/* Row 1 — client name + status/DIR chips + CSAT below chips */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#03DAC6', fontWeight: 'bold' }}>
          {clientName}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={status}
              size="small"
              sx={{ fontSize: '0.65rem', bgcolor: `${getStatusColor(status)}30`, color: getStatusColor(status), height: 20 }}
            />
            {typeof dirScore === 'number' && (
              <Chip
                label={<>DIR<sup style={{ fontSize: '0.55em' }}>©</sup>: {dirScore.toFixed(1)}</>}
                size="small"
                sx={{ fontSize: '0.65rem', bgcolor: `${getDirScoreColor(dirScore)}20`, color: getDirScoreColor(dirScore), height: 20, fontWeight: 'bold' }}
              />
            )}
          </Box>
          {typeof clientSatisfactionScore === 'number' && (
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: getClientSatisfactionColor(clientSatisfactionScore) }}>
              CSAT: {clientSatisfactionScore.toFixed(1)} — {clientSatisfactionScore >= 4.5 ? 'Excellent' : clientSatisfactionScore >= 4.0 ? 'Very Good' : clientSatisfactionScore >= 3.0 ? 'Good' : 'Needs Attention'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Row 2 — Budget hero metric */}
      {(heroValue || budget) && (
        <Box>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', letterSpacing: '0.5px', textTransform: 'uppercase', mb: 0.25 }}>
            Budget Spent
          </Typography>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
            {heroValue || budget}
          </Typography>
          {heroSubline && (
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
              {heroSubline}
            </Typography>
          )}
        </Box>
      )}

      {/* Row 3 — dates */}
      {startDate && endDate && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EventIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: '#64B5F6' }} />
          <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {formattedStartDate} — {formattedEndDate}
          </Typography>
        </Box>
      )}

    </Box>
  );
};

ProjectHeader.propTypes = {
  clientName: PropTypes.string.isRequired,
  status: PropTypes.string,
  budgetSpent: PropTypes.string,
  totalBudget: PropTypes.string,
  budget: PropTypes.string,
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dirScore: PropTypes.number,
  clientSatisfactionScore: PropTypes.number
};

export default ProjectHeader;
